import {
  IonContent,
  IonHeader,
  IonLabel,
  IonList,
  IonPage,
  IonToolbar,
  IonImg,
  IonItem,
  IonIcon,
  IonAlert,
  IonSegment,
  IonSegmentButton,
  IonButton,
  IonRadioGroup,
  IonRadio,
} from '@ionic/react';
import React, { useEffect, useState, useCallback, CSSProperties } from 'react';
import './Dashboard.scss';

import logoSvg from '../../assets/logo.svg';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../store';
import { AppDispatch } from '../../store/store';
import { updateCurrentProfile, updateTemperature, lock, unlock } from '../../store/reducers/bleReducer';
import stepForward from '../../assets/step-forward.svg';
import fastForward from '../../assets/fast-forward.svg';
import stepBackward from '../../assets/step-backward.svg';
import fastBackward from '../../assets/fast-backward.svg';

const MIN_TEMP = 140;
const MAX_TEMP = 220;

const version = require('../../../package.json').version;

interface IDashboardProps {
  readonly displayLockDialog: boolean;
  readonly dismissLockDialogCallback: () => void;
}

const Dashboard: React.FC<IDashboardProps> = (props: IDashboardProps) => {
  const temperatures = useSelector((state: RootState) => state.ble.temperatures);
  const profile = useSelector((state: RootState) => state.ble.profile);
  // const updating = useSelector((state: RootState) => state.ble.updating);
  const battery = useSelector((state: RootState) => state.ble.battery);
  const locked = useSelector((state: RootState) => (state.ble.status & 1) === 1);

  const dispatch = useDispatch<AppDispatch>();

  const [currentProfile, setCurrentProfile] = useState(profile);
  const [tempDialogOpen, setTempDialogOpen] = useState(false);
  const [units, setUnits] = useState(localStorage.getItem('.ghostmv1.units') || 'C');

  const getTempString = (value: number): string => {
    if (units === 'F') {
      return `${Math.round((value * 9) / 5 + 32).toFixed(0)}\u00B0F`;
    } else {
      return `${value.toFixed(0)}\u00B0C`;
    }
  };

  const updateTemperatureDispatch = useCallback(
    (value: number) => {
      const newTemp = [...temperatures.slice(0, profile - 1), value, ...temperatures.slice(profile)];
      dispatch(updateTemperature(newTemp));
    },
    [temperatures, profile, dispatch],
  );

  const setProfile = async (newProfile: number): Promise<void> => {
    if (currentProfile === newProfile) {
      return;
    }
    setCurrentProfile(newProfile);
    const updatedProfile = await dispatch(updateCurrentProfile(newProfile));
    if (updatedProfile !== newProfile) {
      setCurrentProfile(newProfile);
    }
  };

  const getBatteryIcon = (): JSX.Element => {
    //    const batteryLevel = Math.round(battery / 25);
    return (
      <svg width={42} height={18} className="battery" key={`batteryIndicator_${battery}`}>
        <rect
          width={34}
          height={14}
          stroke="#CCC"
          strokeWidth={2}
          x={2}
          y={2}
          rx={2}
          ry={2}
          className="battery-container"
        />
        <rect
          height={12}
          width={Math.round((battery / 100) * 32)}
          x={3}
          y={3}
          rx={2}
          ry={2}
          className="battery-gauge"
        />
        <rect width={4} height={8} x={36} y={6} className="battery-button" />
        {/* <text x={4} y={13} className="battery-text">
        {battery.toFixed(0)}%
      </text> */}
      </svg>
    );
  };

  const getTempDots = (
    profile: number,
    x = 0,
    y = 0,
    viewBox = '0 0 60 12',
    style: CSSProperties = { width: '60px', height: '12px' },
  ): JSX.Element => {
    return (
      <svg x={x} y={y} style={style} viewBox={viewBox} className="profile-dots">
        <circle
          cx="15"
          cy="6"
          r="4"
          fill={
            profile === 3
              ? 'var(--ion-color-warning)'
              : profile === 6
              ? 'var(--ion-color-danger-shade)'
              : 'var(--ion-color-medium)'
          }
        />
        <circle
          cx="30"
          cy="6"
          r="4"
          fill={
            profile === 2 || profile === 3
              ? 'var(--ion-color-warning)'
              : profile === 5 || profile === 6
              ? 'var(--ion-color-danger-shade)'
              : 'var(--ion-color-medium)'
          }
        />
        <circle
          cx="45"
          cy="6"
          r="4"
          fill={profile >= 4 ? 'var(--ion-color-danger-shade)' : 'var(--ion-color-warning)'}
        />
      </svg>
    );
  };

  useEffect(() => {
    if (profile !== currentProfile) {
      setCurrentProfile(profile);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile]);

  const incrementTemp = (increment: number): number => {
    let newTemp = temperatures[profile - 1] + increment;
    if (units === 'F') {
      newTemp = Math.round((temperatures[profile - 1] + (5 / 9) * increment) * 10) / 10;
    }
    if (newTemp < MIN_TEMP) {
      newTemp = MIN_TEMP;
    } else if (newTemp > MAX_TEMP) {
      newTemp = MAX_TEMP;
    }
    return newTemp;
  };

  return (
    <IonPage className="dashboard">
      <IonLabel class="version-label">{version}</IonLabel>
      <IonRadioGroup
        class="units-group"
        value={units}
        onIonChange={(props): void => {
          const newUnits = props.detail.value as string;
          localStorage.setItem('.ghostmv1.units', newUnits);
          setUnits(newUnits);
        }}
      >
        <IonRadio mode="md" value="F" />
        <IonLabel>&deg;F</IonLabel>
        &nbsp;&nbsp;
        <IonRadio mode="md" value="C" />
        <IonLabel>&deg;C</IonLabel>
      </IonRadioGroup>
      <IonHeader className="dashboard-header">
        <IonToolbar className="dashboard-header-toolbar">
          <div slot="start" className="dashboard-header-toolbar-battery-icon">
            {getBatteryIcon()}
          </div>
          <IonImg className="dashboard-header-toolbar-logo" src={logoSvg} />
          <IonIcon slot="end" />
        </IonToolbar>
      </IonHeader>
      <IonContent className="dashboard-content" scrollX={false} scrollY={false}>
        <IonList className="dashboard-content-list" lines="none" hidden={props.displayLockDialog}>
          <IonItem className="dashboard-content-list-item">
            <span className="spacer" />
            <IonButton
              disabled={temperatures[profile - 1] <= MIN_TEMP}
              onClick={(): void => {
                const newTemp = incrementTemp(-5);
                updateTemperatureDispatch(newTemp);
              }}
            >
              <IonIcon icon={fastBackward} />
            </IonButton>
            <IonButton
              disabled={temperatures[profile - 1] <= MIN_TEMP}
              onClick={(): void => {
                const newTemp = incrementTemp(-1);
                updateTemperatureDispatch(newTemp);
              }}
            >
              <IonIcon icon={stepBackward} />
            </IonButton>
            <svg className="profile-svg">
              <rect
                x="2"
                y="2"
                rx="6"
                ry="6"
                width="146"
                height="86"
                fill="transparent"
                stroke="var(--ion-color-primary)"
              />
              <rect
                x="8"
                y="8"
                rx="6"
                ry="6"
                width="134"
                height="74"
                fill="var(--ion-color-primary)"
                style={{ cursor: 'pointer' }}
                onClick={(): void => {
                  setTempDialogOpen(true);
                }}
              />
              <text
                x="32"
                y="55"
                fill="var(--ion-color-primary-contrast)"
                style={{ fontSize: '2em', cursor: 'pointer' }}
                onClick={(): void => {
                  setTempDialogOpen(true);
                }}
              >
                {getTempString(temperatures[profile - 1])}
              </text>
              <text x="65" y="110" fill="var(--ion-color-medium-shade)" className="profile-text">
                {profile === 1
                  ? 'L'
                  : profile === 2
                  ? 'M'
                  : profile === 3
                  ? 'H'
                  : profile === 4
                  ? 'F1'
                  : profile === 5
                  ? 'F2'
                  : profile === 6
                  ? 'C'
                  : ''}
              </text>
              {getTempDots(profile, 45, 120, '0 0 150 150')}
            </svg>
            <IonButton
              disabled={temperatures[profile - 1] >= MAX_TEMP}
              onClick={(): void => {
                const newTemp = incrementTemp(1);
                updateTemperatureDispatch(newTemp);
              }}
            >
              <IonIcon icon={stepForward} />
            </IonButton>
            <IonButton
              disabled={temperatures[profile - 1] >= MAX_TEMP}
              onClick={(): void => {
                const newTemp = incrementTemp(5);
                updateTemperatureDispatch(newTemp);
              }}
            >
              <IonIcon icon={fastForward} />
            </IonButton>
            <span className="spacer" />
          </IonItem>
          <IonItem className="dashboard-content-list-footer1">
            <IonSegment value={currentProfile.toString()}>
              <IonSegmentButton
                value="1"
                onClick={(): void => {
                  setProfile(1);
                }}
              >
                <div>L</div>
                <div>{getTempDots(1)}</div>
                <div>{getTempString(temperatures[0])}</div>
              </IonSegmentButton>
              <IonSegmentButton
                value="2"
                onClick={(): void => {
                  setProfile(2);
                }}
              >
                <div>M</div>
                <div>{getTempDots(2)}</div>
                <div>{getTempString(temperatures[1])}</div>
              </IonSegmentButton>
              <IonSegmentButton
                value="3"
                onClick={(): void => {
                  setProfile(3);
                }}
              >
                <div>H</div>
                <div>{getTempDots(3)}</div>
                <div>{getTempString(temperatures[2])}</div>
              </IonSegmentButton>
            </IonSegment>
          </IonItem>
          <IonItem className="dashboard-content-list-footer2">
            <IonSegment value={currentProfile.toString()}>
              <IonSegmentButton
                value="4"
                onClick={(): void => {
                  setProfile(4);
                }}
              >
                <div>F1</div>
                <div>{getTempDots(4)}</div>
                <div>{getTempString(temperatures[3])}</div>
              </IonSegmentButton>
              <IonSegmentButton
                value="5"
                onClick={(): void => {
                  setProfile(5);
                }}
              >
                <div>F2</div>
                <div>{getTempDots(5)}</div>
                <div>{getTempString(temperatures[4])}</div>
              </IonSegmentButton>
              <IonSegmentButton
                value="6"
                onClick={(): void => {
                  setProfile(6);
                }}
              >
                <div>C</div>
                <div>{getTempDots(6)}</div>
              </IonSegmentButton>
            </IonSegment>
          </IonItem>
        </IonList>
      </IonContent>
      <IonAlert
        animated={false}
        isOpen={tempDialogOpen}
        onDidDismiss={(): void => setTempDialogOpen(false)}
        header={'New Temperature'}
        inputs={[
          {
            name: 'temperature',
            type: 'number',
            label: 'Temperature',
            placeholder: '<New Value>',
            value:
              profile >= 1 && profile <= 6
                ? units === 'C'
                  ? temperatures[profile - 1]
                  : Math.round((temperatures[profile - 1] * 9) / 5 + 32)
                : 0,
            min: units === 'C' ? MIN_TEMP : Math.round((MIN_TEMP * 9) / 5 + 32),
            max: units === 'C' ? MAX_TEMP : Math.round((MAX_TEMP * 9) / 5 + 32),
          },
        ]}
        buttons={[
          {
            text: 'Cancel',
            role: 'cancel',
            cssClass: 'secondary',
          },
          {
            text: 'Ok',
            handler: (values): void => {
              let value = parseInt(values.temperature);
              if (units === 'F') {
                value = ((value - 32) * 5) / 9;
              }
              if (value < MIN_TEMP) {
                value = MIN_TEMP;
              } else if (value > MAX_TEMP) {
                value = MAX_TEMP;
              }
              updateTemperatureDispatch(value);
            },
          },
        ]}
      />
      <IonAlert
        animated={false}
        isOpen={props.displayLockDialog}
        onDidDismiss={(): void => props.dismissLockDialogCallback()}
        header={locked ? 'Unlock?' : 'Lock?'}
        message={`Would you like to ${locked ? 'unlock' : 'lock'} your Ghost MV1 vaporizer?`}
        buttons={[
          {
            text: 'Cancel',
            role: 'cancel',
            cssClass: 'secondary',
          },
          {
            text: 'Ok',
            handler: (): void => {
              if (locked) {
                dispatch(unlock());
              } else {
                dispatch(lock());
              }
            },
          },
        ]}
      />
    </IonPage>
  );
};

export default Dashboard;
